import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { ResourceLinks } from "../../../../constants";
import { makeArrayRandom } from "../../../../utils/learner";
import { useSelector } from "../../../../store";

export default function WebDropDownSelect({ question, onAnswer = () => {} }) {
    const questionData = question;
    const learnerState = useSelector((state) => state.learner || {});
    const data = learnerState?.details?.testLevel === "vfh" ? questionData?.vfh : questionData?.acsf4;
    const imagesResources = ResourceLinks.llnQuizImage;

    const [websiteChoices, setWebsiteChoices] = useState({});
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [selectedKeyPrevious, setSelectedKeyPrevious] = useState(null);

    const [initialChoicesCount, setInitialCount] = useState(Array.isArray(questionData.answer.value)
        ? questionData.answer.value.length
        : Object.keys(questionData.answer.value).length);


    const [availableChoices, setAvailableChoices] = useState(
        makeArrayRandom(data.choices)
    );

    useEffect(() => {
        setWebsiteChoices({});
        setSelectedChoice(null);
        setSelectedKeyPrevious(null);
        setInitialCount(Array.isArray(questionData.answer.value)
            ? questionData.answer.value.length
            : Object.keys(questionData.answer.value).length);
        setAvailableChoices(makeArrayRandom(data.choices));
    }, [question, 
        questionData.answer.value,
        data.choices
    ]);

    const handleChoiceClick = (choice) => {
        setSelectedChoice(choice);
        setSelectedKeyPrevious(null);
    };

    const handleChoiceAssign = (index) => {
        // first we will check if there is an already value
        if (websiteChoices[index] && !selectedChoice) {
            const choice = data.choices.find((c) => c.value === websiteChoices[index].value);
            setSelectedChoice(choice);
            setSelectedKeyPrevious(index);
        }

        if (selectedChoice && selectedChoice?.value !== websiteChoices[index]?.value) {
            let currentWebsiteChoices = {...websiteChoices};
            const previousChoice = currentWebsiteChoices[index];

            if (previousChoice) {
                setAvailableChoices((prevChoices) => [
                    ...prevChoices,
                    { ...previousChoice },
                ]);
            }

            // we will check here if coming from previous answer
            if (selectedKeyPrevious !== null) {
                delete currentWebsiteChoices[selectedKeyPrevious];
                setSelectedKeyPrevious(null);
            }
            // updating answer
            currentWebsiteChoices = {
                ...currentWebsiteChoices,
                [index]: {
                    header: selectedChoice.header,
                    value: selectedChoice.value,
                    isAnswerHeader: selectedChoice.isAnswerHeader ?? false
                },
            }
            setWebsiteChoices(currentWebsiteChoices);

            if (
                typeof questionData.answer.value === "object" &&
                !Array.isArray(questionData.answer.value)
            ) {
                const answerKeys = Object.keys(questionData.answer.value);

                const formattedAnswer = answerKeys.reduce(
                    (acc, key, keyIndex) => {
                        if (currentWebsiteChoices[keyIndex]?.isAnswerHeader) {
                            acc[key] = currentWebsiteChoices[keyIndex]?.header || null;
                        } else {
                            acc[key] = currentWebsiteChoices[keyIndex]?.value || null;
                        }
                        return acc;
                    },
                    {}
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: formattedAnswer,
                });
            } else {
                const updatedAnswer = Object.values(currentWebsiteChoices).map(
                    (choice) => choice.value
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: updatedAnswer,
                });
            }


            setAvailableChoices((prevChoices) =>
                prevChoices.filter(
                    (item) => item.value !== selectedChoice.value
                )
            );

            setSelectedChoice(null);
        }
    };

    const handleRemoveChoice = (index) => {
        const removedChoice = websiteChoices[index];

        if (removedChoice) {
            setWebsiteChoices((prevChoices) => {
                const newChoices = { ...prevChoices };
                delete newChoices[index];

                if (
                    typeof questionData.answer.value === "object" &&
                    !Array.isArray(questionData.answer.value)
                ) {
                    const answerKeys = Object.keys(
                        questionData.answer.value
                    );

                    let canProceed = false;
                    const formattedAnswer = answerKeys.reduce(
                        (acc, key, keyIndex) => {
                            if (newChoices[keyIndex]?.isAnswerHeader) {
                                acc[key] = newChoices[keyIndex]?.header || null;
                            } else {
                                acc[key] = newChoices[keyIndex]?.value || null;
                            }
                            
                            if (acc[key] !== null && acc[key] !== "") {
                                canProceed = true;
                            }

                            return acc;
                        },
                        {}
                    );

                    onAnswer({
                        canProceed,
                        answer: formattedAnswer,
                    });
                } else {
                    const updatedAnswer = Object.values(newChoices).map(
                        (choice) => choice.value
                    );

                    // set to false so that next button will be disabled
                    const canProceed = updatedAnswer.length > 0;
                    onAnswer({
                        canProceed,
                        answer: updatedAnswer,
                    });
                }

                return newChoices;
            });

            setAvailableChoices((prevChoices) => [
                ...prevChoices,
                {
                    header: removedChoice.header,
                    value: removedChoice.value,
                    isAnswerHeader: removedChoice.isAnswerHeader ?? false,
                },
            ]);
        }
    };

    return (
        <Box
            sx={{
                width: "1200px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                
                // for small screen desktop
				"@media (max-width: 1024px), (max-height: 768px)": {
					width: "1050px",
				},

                // for tablet
                "@media (max-width: 800px), (max-height: 600px)": {
					width: "920px",
				},
            }}
        >
            <Box sx={{
                position: "relative",
                textAlign: "center",
            }}>
                <Box
                    sx={{
                        position: "relative",
                        width: "1200px",
                        height: "230px",
                        marginBottom: "10px",
                        border: "1px dashed lightgrey",
                        textAlign: "center",
                        // for small screen desktop
                        "@media (max-width: 1024px), (max-height: 768px)": {
                            width: "1050px",
                        },

                        // for tablet
                        "@media (max-width: 800px), (max-height: 600px)": {
                            width: "920px",
                        },
                    }}
                >
                    <Box
                        width="100%"
                        height="100%"
                        display="flex"
                        justifyContent="space-around"
                        alignItems="center"
                        position="absolute"
                        top="0"
                        left="0"
                        textAlign="center"
                    >
                        {availableChoices.map((choice, index) => (
                            <Box key={index} 
                                className={"selection-button-content " + (selectedChoice?.value === choice.value ? "choice-button-selected" : "")}
                                sx={{
                                    backgroundColor: "#aad6e7",
                                    height: "70px",
                                    width: "120px",
                                    lineHeight: "1em",
                                    fontSize: "20px",
                                    // for small screen desktop
                                    "@media (max-width: 1024px), (max-height: 768px)": {
                                        height: "65px",
                                        fontSize: "19px",
                                        width: "120px",
                                    },

                                    // for tablet
                                    "@media (max-width: 800px), (max-height: 600px)": {
                                        height: "60px",
                                        fontSize: "18px",
                                        width: "110px",
                                    },
                                    borderBottom: "10px solid rgb(52, 148, 186)",
                                }}
                                onClick={() => { handleChoiceClick(choice) }}
                                disabled={selectedChoice === choice}
                            >
                                <Box sx={{ fontWeight: "bold", marginBottom: "4px", }}>
                                    { choice.header }
                                </Box>

                                <Box component="span" sx={{ textAlign: "center", fontWeight: "400" }}>{choice.value}</Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        display: "flex",
                        justifyContent: "space-between",
                        top: "470px",
                        left: "0px",
                        width: "1150px",
                        height: "90px",
                        padding: "12px 10px 10px 10px",
                        // for small screen desktop
                        "@media (max-width: 1024px), (max-height: 768px)": {
                            top: "440px",
                            width: "1000px",
                        },

                        // for tablet
                        "@media (max-width: 800px), (max-height: 600px)": {
                            top: "410px",
                            width: "910px",
                        },
                    }}
                >
                    {[...Array(initialChoicesCount)].map((_, index) => (
                        <Box key={index} 
                            className={"selection-button-border " + (selectedChoice ? "choice-button-selected" : "")}
                            sx={{
                                lineHeight: "0.8em",
                                height: "75px",
                                width: "130px",
                                fontSize: "20px",
                                borderTop: websiteChoices[index] ? "none" : "10px solid rgb(91, 105, 106)",
                                backgroundColor: websiteChoices[index] ? "#aad6e7" : "white",

                                // for small screen desktop
                                "@media (max-width: 1024px), (max-height: 768px)": {
                                    fontSize: "18px",
                                    width: "120px",
                                },

                                // for tablet
                                "@media (max-width: 800px), (max-height: 600px)": {
                                    fontSize: "18px",
                                    height: "80px",
                                    width: "120px",
                                },
                            }}
                        onClick={() => handleChoiceAssign(index)}
                        >
                        {/* For close button */}
                        {websiteChoices[index] && (
                            <Box className="selected-close-btn" 
                                sx={{
                                    top: "0px",
                                    right: "0px",
                                    fontWeight:
                                        typeof questionData.answer.value ===
                                            "object" &&
                                        !Array.isArray(
                                            questionData.answer.value
                                        )
                                            ? "normal"
                                            : "bold",
                                    width: "17.33px",
                                    maxHeight: "20px",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveChoice(index);
                                }}
                            >
                                X
                            </Box>
                        )}
                            <Box sx={{ 
                                display: "inline-flex", 
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                userSelect: "none",
                                pointerEvents: "none",
                                textAlign: "center",
                            }}
                            >
                                    {websiteChoices[index] ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                textAlign: "center",
                                                lineHeight: "1rem",
                                            }}
                                        >
                                            <Box sx={{
                                                fontWeight: "bold",
                                                marginBottom: "5px",
                                            }}
                                            >{websiteChoices[index].header}</Box>
                                            <Box>{websiteChoices[index].value}</Box>
                                        </Box>
                                    ) :
                                    <Box component="span" sx={{
                                        color: "rgb(153, 153, 153)",
                                        fontSize: "64px",
                                    }}></Box>
                                }
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box component="img" src={`${imagesResources}${questionData.acsf4.image}`} alt="Img Calendar"/>
        </Box>
    );
};